import { alpha } from "@mui/material/styles";
import type {
  Aoi,
  AoiEditorMode,
  AoiWorldPoint,
} from "@/features/deployment-dashboard/live-view/model/aoi";
import { worldToFloorplanPixel } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { WorldToPixelTransformAsset } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { GeoScatterBoundary, GeoScatterPoint } from "@/shared/charts";
import type { SecurityLaneStatuses } from "@/features/deployment-dashboard/live-view/utils/derive-security-lane-states";

export interface AoiMapLayers {
  boundaries: GeoScatterBoundary[];
  draftPoints: GeoScatterPoint[];
  /** Clickable "+" markers at each editable edge's midpoint, letting the user insert a new vertex there. */
  insertionPoints: GeoScatterPoint[];
}

export interface DeriveAoiMapLayersOptions {
  /** Current AOI editor mode; `null` when no draft is being edited. */
  editorMode: AoiEditorMode;
  hoveredPointIndex?: number | null;
  /**
   * Forces the draft's closing edge (last point back to the first) to render
   * as a pending, pulsing preview instead of a solid edge, even though there
   * are enough points to close it. Set once an existing AOI's vertex has
   * been removed mid-edit, so the shape stops looking "final" until the user
   * explicitly saves again.
   */
  closurePending?: boolean;
  securityLaneStatuses?: SecurityLaneStatuses;
}

const DRAFT_STROKE_COLOR = "#00bcd4";

function toPixelPoint(
  transform: WorldToPixelTransformAsset,
  point: AoiWorldPoint
): [number, number] {
  const pixel = worldToFloorplanPixel(transform, point.x, point.y);
  return [pixel.x, pixel.y];
}

function midpoint(a: AoiWorldPoint, b: AoiWorldPoint): AoiWorldPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Maps persisted AOIs and the active editor draft from world space into
 * floor-plan pixel space.
 *
 * The draft's closing edge (the implicit segment connecting the last point
 * back to the first) is treated specially:
 * - While creating a brand-new AOI, it never renders as a real edge: the
 *   draft stays an open polyline and, once it has enough points to describe
 *   an area (3+), a separate thin, slowly fading "closing indicator" line
 *   previews where that edge will be drawn once the user saves.
 * - While editing an existing AOI, the shape starts fully closed (all edges,
 *   including the wraparound one, are real and get their own insertion
 *   point). Removing a vertex reverts to the same pending/fading preview as
 *   the create flow (via `closurePending`) until there are enough points to
 *   close it again.
 */
export function deriveAoiMapLayers(
  transform: WorldToPixelTransformAsset | undefined,
  aois: Aoi[],
  selectedAoiId: string | null,
  draftPoints: AoiWorldPoint[],
  options: DeriveAoiMapLayersOptions = { editorMode: null }
): AoiMapLayers {
  if (!transform) return { boundaries: [], draftPoints: [], insertionPoints: [] };

  const {
    editorMode,
    hoveredPointIndex = null,
    closurePending = false,
    securityLaneStatuses = {},
  } = options;

  const boundaries: GeoScatterBoundary[] = aois.map((aoi) => {
    const selected = aoi.id === selectedAoiId;
    const securityLaneStatus = securityLaneStatuses[aoi.id];
    const securityLaneColor =
      securityLaneStatus?.state === "on"
        ? "#2e7d32"
        : securityLaneStatus?.state === "unavailable"
          ? "#757575"
          : "#d32f2f";

    if (securityLaneStatus) {
      return {
        id: `aoi-${aoi.id}`,
        name: aoi.name,
        tooltip: `${aoi.name}: ${securityLaneStatus.state === "unavailable" ? "--" : securityLaneStatus.state.toUpperCase()}`,
        label: {
          text: aoi.name,
          color: "#ffffff",
          backgroundColor: securityLaneColor,
        },
        points: aoi.points.map((point) => toPixelPoint(transform, point)),
        style: {
          fill: alpha(securityLaneColor, 0.18),
          stroke: securityLaneColor,
          strokeWidth: selected ? 4 : 3,
          lineDash: selected ? "dotted" : "solid",
          animated: selected,
        },
      };
    }

    // While actively editing this AOI, the persisted shape is just a static
    // reference outline behind the live, interactive draft overlay below - no
    // marching-ants highlight (that's reserved for a merely *selected*, not
    // being edited, AOI), and a bit more transparent so it reads as secondary.
    const isBeingEdited = editorMode === "edit" && selected;

    if (isBeingEdited) {
      return {
        id: `aoi-${aoi.id}`,
        name: aoi.name,
        points: aoi.points.map((point) => toPixelPoint(transform, point)),
        style: {
          stroke: alpha(aoi.borderColor, 0.45),
          strokeWidth: 2,
          lineDash: "solid" as const,
          animated: false,
        },
      };
    }

    return {
      id: `aoi-${aoi.id}`,
      name: aoi.name,
      tooltip: aoi.name,
      label: {
        text: aoi.name,
        color: "#ffffff",
        backgroundColor: selected ? "rgba(120, 81, 0, 0.92)" : "rgba(4, 28, 57, 0.9)",
      },
      points: aoi.points.map((point) => toPixelPoint(transform, point)),
      style: {
        stroke: aoi.borderColor,
        strokeWidth: selected ? 4 : 2.5,
        lineDash: selected ? "dotted" : aoi.borderStyle,
        animated: selected,
      },
    };
  });

  const pointCount = draftPoints.length;
  // The wraparound edge only renders as a real, solid part of the polygon
  // while editing an already-complete shape that hasn't had a vertex removed
  // since (see `closurePending`). Freshly-drawn shapes never close for real
  // until saved, no matter how many points they have.
  const isClosedForReal = editorMode === "edit" && pointCount >= 3 && !closurePending;

  if (pointCount >= 2) {
    boundaries.push({
      id: "aoi-draft",
      name: "AOI boundary draft",
      points: draftPoints.map((point) => toPixelPoint(transform, point)),
      closed: isClosedForReal,
      style: {
        fill: isClosedForReal ? "rgba(0, 188, 212, 0.15)" : "transparent",
        stroke: DRAFT_STROKE_COLOR,
        strokeWidth: 3,
        lineDash: "dashed",
      },
    });
  }

  if (pointCount >= 3 && !isClosedForReal) {
    boundaries.push({
      id: "aoi-draft-closing",
      name: "AOI pending closing edge",
      points: [
        toPixelPoint(transform, draftPoints[pointCount - 1]),
        toPixelPoint(transform, draftPoints[0]),
      ],
      style: {
        fill: "transparent",
        stroke: DRAFT_STROKE_COLOR,
        strokeWidth: 1.5,
        lineDash: "dashed",
        pulse: true,
      },
    });
  }

  const insertionPoints: GeoScatterPoint[] = [];
  if (editorMode === "edit" && pointCount >= 2) {
    // A real edge exists between every consecutive pair of points, plus the
    // wraparound edge only once the shape is genuinely closed.
    const edgeCount = isClosedForReal ? pointCount : pointCount - 1;
    for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex += 1) {
      const start = draftPoints.at(edgeIndex)!;
      const end = draftPoints.at((edgeIndex + 1) % pointCount)!;
      insertionPoints.push({
        id: `aoi-insert-${edgeIndex}`,
        name: `Add a boundary point on edge ${edgeIndex + 1}`,
        position: toPixelPoint(transform, midpoint(start, end)),
        color: DRAFT_STROKE_COLOR,
        borderColor: "#ffffff",
        borderWidth: 2,
        symbolSize: 18,
      });
    }
  }

  return {
    boundaries,
    draftPoints: draftPoints.map((point, index) => {
      const isHovered = index === hoveredPointIndex;
      return {
        id: `aoi-draft-point-${index}`,
        name: `AOI boundary point ${index + 1}`,
        position: toPixelPoint(transform, point),
        color: isHovered ? "#ffeb3b" : "#00bcd4",
        borderColor: "#ffffff",
        borderWidth: isHovered ? 3 : 2,
        symbolSize: isHovered ? 16 : 12,
      };
    }),
    insertionPoints,
  };
}
